// The monthly shadow-portfolio deck's three tables (analyst_port_performance.py), DS70
// first: returns + risk, the month's attribution and the cumulative attribution. Same
// shading rules as the deck: return cells green / red against DS70 in that column (the
// DS70 row and the risk columns stay white); effects shade on their sign, white when
// they round to 0.00%.
import React from 'react';
import { NEG_FILL, POS_FILL, fmtDmy, fmtMonth } from './apUtils';

const pct2 = (v) => (v === null || v === undefined ? '-' : `${(Math.abs(v) < 0.005 ? 0 : v).toFixed(2)}%`);
const signFill = (v) => (v === null || v === undefined || Math.abs(v) < 0.005 ? undefined : v > 0 ? POS_FILL : NEG_FILL);

export function ReturnsRiskTable({ deck }) {
  const ds70 = deck.rows.find((r) => r.kind === 'benchmark');
  return (
    <table className="ap-deck-table">
      <thead>
        <tr>
          <th className="ap-deck-name">Portfolio</th>
          {deck.windows.map((w) => <th key={w}>{w}</th>)}
          <th>Vol (p.a.)</th><th>TE (p.a.)</th><th>Beta</th>
        </tr>
      </thead>
      <tbody>
        {deck.rows.map((r) => (
          <tr key={r.code} className={r.kind === 'benchmark' ? 'ap-deck-bench' : undefined}>
            <td className="ap-deck-name">{r.name}</td>
            {deck.windows.map((w) => {
              const v = r.returns[w];
              const d = ds70?.returns[w];
              const fill = r.kind === 'benchmark' || v == null || d == null || v === d ? undefined : v > d ? POS_FILL : NEG_FILL;
              return <td key={w} style={fill ? { background: fill } : undefined}>{pct2(v)}</td>;
            })}
            <td>{pct2(r.vol)}</td>
            <td>{r.te == null ? '-' : pct2(r.te)}</td>
            <td>{r.beta == null ? '-' : r.beta.toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EffectsTable({ deck, part, title }) {
  return (
    <table className="ap-deck-table ap-deck-effects">
      <thead>
        <tr>
          <th className="ap-deck-name">{title}</th>
          <th>Allocation Effect</th><th>Selection Effect</th><th>Total Effect</th>
        </tr>
      </thead>
      <tbody>
        {deck.rows.map((r) => {
          const e = r[part];
          return (
            <tr key={r.code} className={r.kind === 'benchmark' ? 'ap-deck-bench' : undefined}>
              <td className="ap-deck-name">{r.name}</td>
              {['alloc', 'sel', 'total'].map((k) => {
                const fill = r.kind === 'benchmark' ? undefined : signFill(e[k]);
                return <td key={k} style={fill ? { background: fill } : undefined}>{pct2(e[k])}</td>;
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function AttributionTables({ deck }) {
  return (
    <div className="ap-deck-effects-row">
      <EffectsTable deck={deck} part="month" title={`For month ending ${fmtDmy(deck.month)}`} />
      <EffectsTable deck={deck} part="cumulative" title={`Cumulative to ${fmtDmy(deck.month)}`} />
    </div>
  );
}

/** What a dash in the returns table means: windows whose months' reports are not loaded. */
export function DeckGaps({ deck }) {
  const gaps = deck.window_gaps || {};
  const months = [...new Set(Object.values(gaps).flat())].sort();
  const lines = [];
  if (months.length) {
    lines.push(`Windows that need a month whose MONTHLY report is not in the attribution DB show "-": `
      + `${months.map(fmtMonth).join(', ')} (${Object.keys(gaps).join(', ')}).`);
  }
  if (deck.monthly_missing?.length) {
    lines.push(`No ${fmtMonth(deck.month)} MONTHLY report for ${deck.monthly_missing.join(', ')}, so its month attribution shows "-".`);
  }
  lines.push(`Since inception ${fmtDmy(deck.inception)} (${deck.n_months} months). 1–12 Month returns chain the monthly reports `
    + '(cumulative); 3/5/10 Year appear once that many months exist (annualised). S.I and risk are realised since '
    + 'inception from the SI reports.');
  return <div className="ap-footnote">{lines.map((l) => <p key={l}>{l}</p>)}</div>;
}
